import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import uniqueId from 'lodash/uniqueId';
import classnames from 'classnames';
import { Carrier } from '../data/carriers';
import { LabelPreviewHelper, NetStampSeries, SheetType } from '../data/print';

import Thumbnail from './Thumbnail.component';

export default class LabelPreview extends Component {
	constructor(props) {
		super(props);

		this.state = {
			sheet: null,
			sheetData: {},

			// 1: printed, 0: available
			history: [],

			// first selected thumbnail's position (start at 1)
			startPosition: null,

			// number of thumbnails selected
			qty: 1,

			// max quantity the sheet supports
			maxQty: 1,
		};
	}

	componentDidMount() {
		this.updateState();
	}

	componentDidUpdate(prevProps) {
		const { sheet, series, history, qty } = this.props;

		if (
			history !== prevProps.history ||
			sheet !== prevProps.sheet ||
			series !== prevProps.series ||
			qty !== prevProps.qty
		) {
			this.updateState();
		}
	}

	/**
	 * Build a 2-dimensional array of thumbnails' state and metadata for rendering.
	 * [
	 *     [1-1], [1-2], [1-3], ...
	 * 	   [2-1], [2-2], [2-3], ...
	 *     ...
	 * ]
	 *
	 * @param {array} history
	 * @param {number} qty
	 * @returns {array}
	 */
	buildThumbnailsState = () => {
		const { maxQty, sheetData } = this.state;
		const { sheet, type, width, height, numCols } = sheetData;
		const uniqueKey = uniqueId();
		const thumbnails = [];

		for (let idx = 0; idx < maxQty; idx++) {
			const row = Math.floor(idx / numCols);
			const col = idx % numCols;
			const position = idx + 1;

			if (!isArray(thumbnails[row])) {
				thumbnails[row] = [];
			}

			thumbnails[row].push({
				key: `${uniqueKey}_${position}`,
				position,

				// sheet and series are used to determine the thumbnail image
				sheet,
				series: type,

				// State => 0: available, 1: printed, 2: selected
				state: this.calculateThumbnailState(position),

				// check if dynamic thumbnail image is available
				image: this.getDynamicThumbnailImage(position),

				// thumbnail dimensions
				width: isNumber(width) ? width : null,
				height: isNumber(height) ? height : null,

				// coordinate
				row,
				col,
			});
		}

		return thumbnails;
	};

	/**
	 * Look for the dynamic thumbnail image from `labels` props, and returns
	 * the image URL.
	 *
	 * @param {number} position Start from 1.
	 * @returns {string|null}
	 */
	getDynamicThumbnailImage = position => {
		const { labels } = this.props;
		const { startPosition, qty } = this.state;

		// dynamic thumbnail image is only applicable for selected thumbnails
		if (position >= startPosition && position < startPosition + qty) {
			if (isString(labels) && labels !== '') {
				return labels;
			} else if (isArray(labels) && labels.length > 0) {
				return isString(labels[position - startPosition])
					? labels[position - startPosition]
					: null;
			}
		}

		return null;
	};

	/**
	 * Get static LabelPreview data.
	 * @param {string} sheet
	 * @param {string} series
	 * @returns {object}
	 */
	getSheetData = (sheet, series) => {
		//  default series to 'usps' for shipping label or roll sheet if the value is missing
		const seriesVal =
			!series && (sheet === SheetType.ShippingLabel || sheet === SheetType.Roll)
				? Carrier.USPS
				: series;

		const sheetData = LabelPreviewHelper.getData(sheet, seriesVal);

		return isObject(sheetData)
			? sheetData
			: {
					sheet,
					type: null,
					numCols: 1,
					numRows: 1,
			  };
	};

	/**
	 * Calculate the start position of first selected thumbnail.
	 *
	 * @param {number} startPosition Start from 1.
	 * @param {array} history Print history.
	 * @param {number} maxQty Max count the sheet supports.
	 * @returns {number}
	 */
	calculateStartPosition = (startPosition, history, maxQty) => {
		// based on history array
		if (isArray(history) && history.length > 0) {
			// look for the first `0` in the array
			for (let idx = 0; idx < history.length; idx++) {
				if (history[idx] === 0) {
					return idx + 1;
				}
			}
		}
		if (!isNumber(startPosition)) {
			return 1;
		}

		// based on `startPosition`, the start position can't go beyond what sheet supports
		return startPosition % maxQty === 0 ? maxQty : startPosition % maxQty;
	};

	/**
	 * Calculate the effective quantity. For instance,
	 *   - start position = 20
	 *   - max quantity = 25 (B-series netstamp sheet)
	 *   - qty = 25 ("Print All" checkbox is checked)
	 *
	 *  => effective quantity is 5.
	 *
	 * @param {number} startPosition Start from 1.
	 * @param {number} qty Selection count for printing.
	 * @param {number} maxQty Max count the sheet supports.
	 * @returns {number}
	 */
	calculateQuantity = (startPosition, qty, maxQty) => {
		const quantity = isNumber(qty) ? qty : 1;

		if (startPosition + quantity - 1 <= maxQty) {
			return quantity;
		} else {
			return maxQty - startPosition + 1;
		}
	};

	/**
	 * Calculate thumbnail's state based on its position. State includes:
	 * 	 0: available
	 *   1: printed
	 *   2: selected
	 *
	 * @param {number} position Position starts at 1.
	 * @returns {number}
	 */
	calculateThumbnailState = position => {
		const { startPosition, history, qty } = this.state;
		const state = {
			available: 0,
			printed: 1,
			selected: 2,
		};

		if (position >= startPosition && position < startPosition + qty) {
			return state.selected;
		}

		return history[position - 1] === 1 ? state.printed : state.available;
	};

	/**
	 * This method is called to redraw the preview graph.
	 */
	updateState = () => {
		const { sheet, series, history, qty, startPosition } = this.props;

		// get sheet static data based on sheet and series
		const sheetData = this.getSheetData(sheet, series);

		// calculate the max number of thumbnails on the sheet
		const maxQty = sheetData.numCols * sheetData.numRows;

		// find out the effective start position
		const calculatedStartPos =
			sheet === SheetType.Roll ||
			(sheet === SheetType.NetStamps && series === NetStampSeries.RollTypeSorD)
				? 1
				: this.calculateStartPosition(startPosition, history, maxQty);

		// calculate the effective quantity
		const calculatedQty = this.calculateQuantity(calculatedStartPos, qty, maxQty);

		this.setState({
			sheet,
			sheetData,
			startPosition: calculatedStartPos,
			history: isArray(history) ? history : [],
			qty: calculatedQty,
			maxQty,
		});
	};

	/**
	 * Recalculate the thumbails' state and effective quantity, and
	 * notify the caller about the new position and quantity.
	 *
	 * @param {object} event
	 */
	onSelect = ({ position }) => {
		const { onSelect, isPositionValid, isReprint, series } = this.props;
		const { startPosition, qty, maxQty, sheet } = this.state;
		const isNetStamps = sheet === SheetType.NetStamps;
		let newStartPos = isNumber(position) ? position : startPosition;
		let newQty = this.calculateQuantity(newStartPos, qty, maxQty);
		let isValid = true;
		let errorMessage = '';

		// for netstamp reprints, check slot validity on select to see if any stamps overflow
		// the original quantity must also be maintained
		if (isFunction(isPositionValid) && isNetStamps && isReprint) {
			({ isValid, errorMessage } = isPositionValid(newStartPos, qty, sheet, series));
			newStartPos = isValid ? newStartPos : 1;
			newQty = qty;
		}

		this.setState(
			{
				startPosition: newStartPos,
				qty: newQty,
			},
			() => {
				if (isFunction(onSelect)) {
					onSelect({
						startPosition: newStartPos,
						qty: newQty,
						isValid,
						errorMessage,
					});
				}
			}
		);
	};

	renderNetstampsPreview = () => {
		const thumbnails = this.buildThumbnailsState();
		const { sheetData, qty } = this.state;
		const { type } = sheetData;

		if (isString(type)) {
			return (
				<div
					className={classnames('sheet netstamps-sheet', {
						[type]: true,
						[`${type}-${qty}`]: type === NetStampSeries.RollTypeSorD,
					})}
				>
					{type !== NetStampSeries.RollTypeSorD ? (
						<div className="thumbnails-container">
							{thumbnails.map((rowData, idx) => (
								<div className="row" key={`${rowData[0].key}_${idx}`}>
									{rowData.map(({ key, ...thumbnailData }) => (
										<Thumbnail
											key={key}
											{...thumbnailData}
											onSelect={this.onSelect}
											selectable
										/>
									))}
								</div>
							))}
						</div>
					) : null}
				</div>
			);
		}

		return <div className="sheet netstamps-sheet default" />;
	};

	renderShippingLabelPreview = () => {
		const thumbnails = this.buildThumbnailsState();

		return (
			// sheet
			<div className="sheet shipping-label-sheet">
				{/* thumbnails */}
				<div className="thumbnails-container">
					<div className="row">
						{thumbnails[0].map(({ key, ...thumbnailData }) => (
							<Thumbnail
								key={key}
								{...thumbnailData}
								onSelect={this.onSelect}
								selectable
							/>
						))}
					</div>
				</div>
			</div>
		);
	};

	renderRollPreview = () => {
		const { sheet, sheetData } = this.state;
		const { type, width, height } = sheetData;

		// position is always 1 for roll preview
		const position = 1;

		return (
			// sheet
			<div className="sheet roll-sheet">
				{/* thumbnails */}
				<div className="thumbnails-container">
					<Thumbnail
						sheet={sheet}
						series={type}
						width={width}
						height={height}
						image={this.getDynamicThumbnailImage(position)}
						selectable={false}
					/>
				</div>
			</div>
		);
	};

	renderEnvelopePreview = () => {
		const { sheetData } = this.state;
		const { sheet, type } = sheetData;

		// position is always 1 for envelope preview
		const position = 1;

		return (
			// sheet
			<div className="sheet envelope-sheet">
				{/* thumbnails */}
				<div className="thumbnails-container">
					<Thumbnail
						sheet={sheet}
						series={type}
						image={this.getDynamicThumbnailImage(position)}
						selectable={false}
					/>
				</div>
			</div>
		);
	};

	renderCertifiedMailPreview = () => {
		const { sheetData } = this.state;
		const { type } = sheetData;
		return (
			// sheet
			<div className={`sheet certified-mail-sheet cm-${type}`}>{/* thumbnails */}</div>
		);
	};

	/**
	 * Render "Requires {number} of label sheet(s)" tip.
	 */
	renderRequireSheetTip() {
		const { showRequireSheetTip } = this.props;
		const { sheet, startPosition, maxQty, qty } = this.state;
		const numOfSheet = Math.ceil((startPosition + qty - 1) / maxQty);

		if (showRequireSheetTip) {
			// tooltip is only applicable to shipping label sheets and rolls
			if (sheet === SheetType.ShippingLabel || sheet === SheetType.Roll) {
				return (
					<p className="tip">
						Requires {numOfSheet} label sheet{numOfSheet > 1 ? 's' : ''}
					</p>
				);
			}
		}

		return null;
	}

	render() {
		const { sheet } = this.state;

		return (
			// project - module - component
			<div className="common-print-label-preview">
				{sheet === SheetType.NetStamps && this.renderNetstampsPreview()}
				{sheet === SheetType.ShippingLabel && this.renderShippingLabelPreview()}
				{sheet === SheetType.Roll && this.renderRollPreview()}
				{sheet === SheetType.Envelope && this.renderEnvelopePreview()}
				{sheet === SheetType.CertifiedMail && this.renderCertifiedMailPreview()}
				{this.renderRequireSheetTip()}
			</div>
		);
	}
}

LabelPreview.defaultProps = {
	sheet: SheetType.NetStamps,
	qty: 1,
	showRequireSheetTip: false,
};

LabelPreview.propTypes = {
	// sheet
	sheet: PropTypes.string,

	// series
	series: PropTypes.string,

	// thumbnails selection count
	qty: PropTypes.number,

	// actual label image(s)
	labels: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

	// `true` to show Tracking Cost
	history: PropTypes.array,

	// Static label image settings
	startPosition: PropTypes.number,

	// `true` to display "Requires {number} of label sheet(s)"
	showRequireSheetTip: PropTypes.bool,

	// Event handler for thumbnail selection
	onSelect: PropTypes.func,
};
